import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Newspaper, Clock } from "lucide-react";
import { api } from "../../lib/api";
import SafeImage from "../SafeImage";
import BannerAd from "../BannerAd";
import { planBannerSlots } from "../../lib/bannerSlots";

const ago = (iso) => {
  const h = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 36e5));
  if (!iso || Number.isNaN(h)) return "";
  if (h < 1) return "agora";
  if (h < 24) return `há ${h}h`;
  return `há ${Math.round(h / 24)}d`;
};

function NewsCard({ p, big = false }) {
  return (
    <Link to={`/noticia/${p.slug}`} data-testid={`home-news-card-${p.id}`}
      className={`group block rounded-2xl overflow-hidden bg-white soft-shadow hover:-translate-y-1 transition-transform duration-300 ${big ? "" : "w-64 md:w-auto shrink-0"}`}>
      <div className={`relative overflow-hidden ${big ? "h-56 md:h-72" : "h-36"}`}>
        <SafeImage src={p.image} category={p.category} alt={p.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        {p.guia_news_ai && <span className="absolute top-2 left-2 rounded-full bg-slate-900/80 text-white text-[10px] font-semibold px-2 py-0.5">Guia News AI</span>}
      </div>
      <div className="p-4">
        <p className={`font-heading font-bold text-slate-900 leading-snug ${big ? "text-lg md:text-xl line-clamp-3" : "text-sm line-clamp-3"}`}>{p.title}</p>
        {big && p.summary && <p className="text-sm text-slate-500 mt-2 line-clamp-2">{p.summary}</p>}
        <p className="text-[11px] text-slate-400 mt-2 flex items-center gap-1"><Clock className="w-3 h-3" />{ago(p.published_at)}{p.source ? ` · ${p.source}` : ""}</p>
      </div>
    </Link>
  );
}

function Block({ block }) {
  const href = block.key === "latest" ? "/noticias" : `/noticias?category=${block.category}`;
  const [idx, setIdx] = useState(0);
  const isLatest = block.key === "latest";
  const spin = Math.min(5, block.items.length);
  useEffect(() => {
    if (!isLatest || spin < 2) return undefined;
    const t = setInterval(() => setIdx((i) => (i + 1) % spin), 7000);
    return () => clearInterval(t);
  }, [isLatest, spin]);
  const first = block.items[idx];
  const rest = block.items.filter((_, i) => i !== idx);
  return (
    <div data-testid={`home-news-block-${block.key}`}>
      <div className="flex items-end justify-between mb-4">
        <h3 className="font-heading font-bold text-xl md:text-2xl text-slate-900">{block.label}</h3>
        <Link to={href} data-testid={`home-news-more-${block.key}`} className="text-sm font-semibold text-primary flex items-center gap-1">Ver mais <ArrowRight className="w-4 h-4" /></Link>
      </div>
      {block.key === "latest" ? (
        <div className="grid md:grid-cols-2 gap-5">
          <motion.div key={first.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }} data-testid="home-news-rotating">
            <NewsCard p={first} big />
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{rest.slice(0, 4).map((p) => <NewsCard key={p.id} p={p} />)}</div>
        </div>
      ) : (
        <div className="flex md:grid md:grid-cols-3 lg:grid-cols-6 gap-4 overflow-x-auto scrollbar-hide pb-2 -mx-4 px-4 md:mx-0 md:px-0">
          {block.items.map((p) => <NewsCard key={p.id} p={p} />)}
        </div>
      )}
    </div>
  );
}

export default function HomeNewsBlocks({ banners = [] }) {
  const ref = useRef(null);
  const [blocks, setBlocks] = useState(null);
  const [seed] = useState(() => Math.floor(Math.random() * 2_000_000_000));

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const obs = new IntersectionObserver((entries) => {
      if (!entries[0].isIntersecting) return;
      obs.disconnect();
      api.get("/home/news-blocks", { params: { seed } }).then(({ data }) => setBlocks(data.blocks || [])).catch(() => setBlocks([]));
    }, { rootMargin: "400px" });
    obs.observe(el);
    return () => obs.disconnect();
  }, [seed]);

  // Banner after a block when an organic slot (every 8–10 items) falls inside it.
  const afterBlock = useMemo(() => {
    if (!blocks) return {};
    const total = blocks.reduce((n, b) => n + b.items.length, 0);
    const slots = planBannerSlots(total, banners.filter((b) => b.iab_format !== "mobile"), seed);
    const out = {};
    let end = 0;
    blocks.forEach((b, i) => {
      const start = end;
      end += b.items.length;
      const hit = Object.keys(slots).map(Number).find((s) => s >= start && s < end);
      if (hit !== undefined && i < blocks.length - 1) out[i] = slots[hit];
    });
    return out;
  }, [blocks, banners, seed]);

  if (blocks && blocks.length === 0) return null;
  return (
    <section ref={ref} id="noticias" data-testid="home-news-blocks" className="max-w-7xl mx-auto px-4 md:px-8 pt-10 pb-16 md:pt-14 md:pb-20" style={{ minHeight: blocks ? undefined : 600 }}>
      <div className="max-w-2xl mb-10">
        <p className="text-xs font-semibold tracking-widest text-primary uppercase mb-2 flex items-center gap-1.5"><Newspaper className="w-4 h-4" /> Notícias de Jampa</p>
        <h2 className="font-heading font-bold text-3xl md:text-4xl text-slate-900">O que está acontecendo na cidade</h2>
        <p className="text-slate-500 mt-3">As notícias mais recentes de João Pessoa, organizadas por tema.</p>
        <Link to="/noticias" data-testid="home-news-center-link" className="inline-flex items-center gap-1.5 mt-4 rounded-full bg-primary text-white px-5 py-2.5 text-sm font-semibold hover:opacity-90 transition-opacity">
          Central de notícias <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
      {!blocks && (
        <div className="grid md:grid-cols-3 gap-5" data-testid="home-news-skeleton">
          {[0, 1, 2].map((i) => <div key={i} className="h-64 rounded-2xl bg-slate-100 animate-pulse" />)}
        </div>
      )}
      <div className="space-y-14">
        {(blocks || []).map((b, i) => (
          <React.Fragment key={b.key}>
            <Block block={b} />
            {afterBlock[i] && <BannerAd banner={afterBlock[i]} />}
          </React.Fragment>
        ))}
      </div>
    </section>
  );
}
